//Dependencies
import React, { useEffect, useMemo, useState } from 'react';
import { FiSearch, FiUsers, FiUserPlus, FiBookOpen, FiStar, FiTag, FiLogOut, FiX, FiMail } from 'react-icons/fi';
import { PageHeader, Kpi, Badge, Notice, Modal, Skeleton, DateRange } from '../Components/UI';
import DataTable, { Column } from '../Components/UI/DataTable';
import Pagination from '../Components/UI/Pagination';
import Drawer from '../Components/UI/Drawer';
import { useUrlState } from '../Components/Hooks/useUrlState';
import { useDebounce } from '../Components/Hooks/useDebounce';
import { useCachedQuery, prefetchQuery, clearQueryCache } from '../Components/Hooks/useCachedQuery';
import { useReadersSession } from '../Components/Hooks/useReadersSession';
import { ActivitySummaryPanel, ReaderActivitySection, StoriesOpenedSection, WordsTappedSection } from '../Components/Users/ReadingActivity';
import { fetchReaders, fetchReaderStats, fetchReader, createCouponCode, ReaderQuery } from '../Utils/ReaderUsersAPI';
import { setReadersSession } from '../Utils/ReadersAPI';
import { ApiError } from '../Utils/api';
import { ReaderRow } from '../Models/AdminUsers';
import { formatDate, formatDateTime, formatNumber, gradeLabel, initialsOf, timeAgo, withAgo } from '../Utils/Format';
import { SignupSiteBadge, SignupSiteCell } from '../Components/Users/SignupSite';
import { AccessBadge } from '../Components/Users/AccessBadge';

const DEFAULTS = {
  page: '1',
  limit: '25',
  search: '',
  type: 'parent',
  plan: 'all',
  activity: 'all',
  site: 'readers',
  read: '',
  finished: '',
  from: '',
  to: '',
  sort: 'created',
  dir: 'desc',
  user: '',
  days: '30',
};

const planTone = (plan: string) => (plan === 'free' ? 'free' : plan === 'school' ? 'school' : 'paid');
const PlanBadge: React.FC<{ plan: string }> = ({ plan }) => (
  <Badge tone={planTone(plan || 'free')}>{(plan || 'free').replace(/^\w/, (c) => c.toUpperCase())}</Badge>
);

const ReadingPortalUsers: React.FC = () => {
  const session = useReadersSession();
  const [couponOpen, setCouponOpen] = useState(false);

  const disconnect = () => {
    setReadersSession(null);
    clearQueryCache('rd:');
  };

  return (
    <div>
      <PageHeader
        title="Reading users"
        description="No Fuss Readers parents, teachers and students. See how much they read, then search, filter and click a row for children and reading history."
        actions={
          session.token ? (
            <>
              <button className="btn--secondary" onClick={() => setCouponOpen(true)}>
                <FiTag /> Create coupon code
              </button>
              <button className="btn--ghost" onClick={disconnect} title={`Signed in to Readers as ${session.email || 'admin'}`}>
                <FiLogOut /> Disconnect Readers
              </button>
            </>
          ) : null
        }
      />
      <ReadersUsersView />
      {couponOpen && <CouponModal onClose={() => setCouponOpen(false)} />}
    </div>
  );
};

const ReadersUsersView: React.FC = () => {
  const [state, setState] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(state.search);
  const debouncedSearch = useDebounce(searchInput.trim(), 300);

  useEffect(() => {
    if (debouncedSearch !== state.search) setState({ search: debouncedSearch, page: '1' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);
  useEffect(() => {
    if (state.search !== searchInput.trim()) setSearchInput(state.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.search]);

  const stats = useCachedQuery('rd:stats', (signal) => fetchReaderStats(signal), 60000);
  // Readers sign ups only by default, once accounts carry a sign-up label; until then show everyone.
  const labelsReady = !!(stats.data && stats.data.siteNotRecorded === 0);
  const effectiveSite = state.site === 'readers' && !labelsReady ? 'all' : state.site;

  const query: ReaderQuery = useMemo(
    () => ({
      page: Math.max(1, parseInt(state.page, 10) || 1),
      limit: Math.min(100, Math.max(10, parseInt(state.limit, 10) || 25)),
      search: state.search,
      type: state.type,
      plan: state.plan,
      activity: state.activity,
      from: state.from,
      to: state.to,
      sort: state.sort,
      dir: state.dir === 'asc' ? 'asc' : 'desc',
      site: effectiveSite,
      read: state.read,
      finished: state.finished,
    }),
    [state.page, state.limit, state.search, state.type, state.plan, state.activity, effectiveSite, state.read, state.finished, state.from, state.to, state.sort, state.dir],
  );
  const list = useCachedQuery(`rd:list:${JSON.stringify(query)}`, (signal) => fetchReaders(query, signal));

  useEffect(() => {
    if (!list.data || list.loading || query.page >= list.data.totalPages) return;
    const next = { ...query, page: query.page + 1 };
    prefetchQuery(`rd:list:${JSON.stringify(next)}`, (signal) => fetchReaders(next, signal));
  }, [list.data, list.loading, query]);

  const error = list.error || stats.error;
  const forbidden = error instanceof ApiError && error.status === 403;
  const signInAgain = error instanceof ApiError && error.status === 401;
  const notDeployed = error instanceof ApiError && error.status === 404;

  const filtersActive = !!(state.search || state.plan !== 'all' || state.activity !== 'all' || state.site !== 'readers' || state.read || state.finished || state.from || state.to || state.type !== 'parent');
  const clearFilters = () => {
    setSearchInput('');
    setState({ search: '', plan: 'all', activity: 'all', site: 'readers', read: '', finished: '', from: '', to: '', type: 'parent', page: '1' });
  };
  const onSort = (sortKey: string) => {
    if (state.sort === sortKey) setState({ dir: state.dir === 'desc' ? 'asc' : 'desc', page: '1' });
    else setState({ sort: sortKey, dir: sortKey === 'email' ? 'asc' : 'desc', page: '1' });
  };

  const columns: Column<ReaderRow>[] = [
    {
      key: 'user',
      header: 'User',
      sortKey: 'email',
      render: (user) => (
        <div className="cell-user">
          <span className="avatar">{initialsOf(user.name, user.email)}</span>
          <div style={{ minWidth: 0 }}>
            <strong>{user.name || user.email || 'Unnamed'}</strong>
            <span>{user.name ? user.email || '–' : user.accountType}</span>
          </div>
        </div>
      ),
      skeleton: (
        <div className="cell-user">
          <Skeleton circle width="3.4rem" height="3.4rem" />
          <div style={{ flex: 1 }}>
            <Skeleton width="14rem" block />
            <div style={{ height: '0.6rem' }} />
            <Skeleton width="20rem" block />
          </div>
        </div>
      ),
    },
    {
      key: 'site',
      header: 'Signed up on',
      render: (user) =>
        user.accountType === 'student' ? <span className="muted">–</span> : <SignupSiteCell site={user.signupSite} alsoOnOther={user.alsoOnOtherSite} other="worksheets" />,
    },
    { key: 'type', header: 'Type', render: (user) => <span style={{ textTransform: 'capitalize' }}>{user.accountType}</span> },
    { key: 'plan', header: 'Plan', render: (user) => <AccessBadge plan={user.plan} paying={user.paying} /> },
    { key: 'stories', header: 'Stories opened', className: 'num', sortKey: 'stories', render: (user) => formatNumber(user.storiesOpened) },
    {
      key: 'activity',
      header: 'Last story',
      sortKey: 'activity',
      render: (user) => (user.lastStoryAt ? <span title={formatDateTime(user.lastStoryAt)}>{timeAgo(user.lastStoryAt)}</span> : <span className="muted">Never</span>),
    },
    { key: 'children', header: 'Children', className: 'num', render: (user) => formatNumber(user.childrenCount) },
    { key: 'created', header: 'Signed up', sortKey: 'created', render: (user) => <span title={formatDateTime(user.createdAt)}>{formatDate(user.createdAt)}</span> },
  ];

  if (forbidden || signInAgain) {
    return (
      <Notice tone="error">
        Reading users load with your dashboard login. Sign out and sign back in once to load them.
      </Notice>
    );
  }

  const s = stats.data;
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const CLEARED = { search: '', plan: 'all', activity: 'all', site: 'readers', read: '', finished: '', from: '', to: '', type: 'parent', page: '1' };
  const scrollToTable = () => setTimeout(() => document.querySelector('.table-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  const showCard = (filters: Record<string, string>) => {
    setSearchInput('');
    setState({ ...CLEARED, ...filters });
    scrollToTable();
  };
  const cardActive = (filters: Record<string, string>) =>
    Object.keys(CLEARED).every((key) => (key in filters ? state[key as keyof typeof state] === filters[key] : key === 'page' || state[key as keyof typeof state] === (CLEARED as any)[key]));
  const days = [7, 30, 90].indexOf(parseInt(state.days, 10)) >= 0 ? parseInt(state.days, 10) : 30;
  return (
    <>
      <ActivitySummaryPanel
        days={days}
        onDays={(value) => setState({ days: String(value) })}
        onOpenReader={(id) => setState({ user: id })}
        revealed={state.read ? { read: parseInt(state.read, 10), finished: state.finished === '1' } : null}
        onReveal={(filter) => {
          if (!filter) setState({ read: '', finished: '', page: '1' });
          else setState({ ...CLEARED, read: String(filter.read), finished: filter.finished ? '1' : '', type: 'all', site: 'all' });
          scrollToTable();
        }}
      />
      {notDeployed && (
        <Notice style={{ marginBottom: '1.6rem' }}>
          The Readers server update with the admin users API is not deployed yet. Deploy the
          <code> dashboard-users-api</code> branch of study-portal-server to load reading users here.
        </Notice>
      )}
      {error && !notDeployed && (
        <Notice tone="error" style={{ marginBottom: '1.6rem' }}>
          Could not load reading users: {error.message}
        </Notice>
      )}
      <div className="grid grid--kpi" style={{ marginBottom: '2rem' }}>
        <Kpi
          label={labelsReady ? 'Readers sign ups' : 'Parent accounts'}
          onClick={() => showCard({})}
          active={cardActive({})}
          icon={<FiUsers />}
          value={formatNumber(s ? (labelsReady ? s.signedUpHere : s.parents) : undefined)}
          loading={stats.loading && !s}
          sub={s ? (labelsReady ? `${formatNumber(s.fromWorksheets)} more came from worksheets (hidden)` : 'Includes accounts created by worksheets sign ups') : ' '}
        />
        <Kpi label="New this week" onClick={() => showCard({ from: weekAgo })} active={cardActive({ from: weekAgo })} icon={<FiUserPlus />} tone="coral" value={formatNumber(s && s.newLast7)} loading={stats.loading && !s} sub={s ? `${formatNumber(s.newLast30)} in the last 30 days` : ' '} />
        <Kpi label="Opened a story" onClick={() => showCard({ activity: 'with' })} active={cardActive({ activity: 'with' })} icon={<FiBookOpen />} tone="green" value={formatNumber(s && s.withActivity)} loading={stats.loading && !s} sub="Parents with reading activity" />
        <Kpi
          label="Paying (Stripe)"
          onClick={() => showCard({ plan: 'paying', type: 'all', site: 'all' })}
          active={cardActive({ plan: 'paying', type: 'all', site: 'all' })}
          icon={<FiStar />}
          tone="amber"
          value={formatNumber(s ? (s.payingStripe ?? s.paid) : undefined)}
          loading={stats.loading && !s}
          sub={s && s.freeAccess !== undefined && s.freeAccess !== null ? `${formatNumber(s.freeAccess)} have free access (tutors, codes)` : s ? `${formatNumber(s.free)} on the free plan` : ' '}
        />
      </div>
      <div className="table-card">
        <div className="toolbar">
          <div className="input-icon">
            <FiSearch />
            <input type="search" placeholder="Search by email, username or name" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} aria-label="Search reading users" />
          </div>
          <div className="chips" role="group" aria-label="Account type">
            {['parent', 'teacher', 'student', 'all'].map((value) => (
              <button key={value} className={state.type === value ? 'is-active' : ''} onClick={() => setState({ type: value, page: '1' })}>
                {value === 'all' ? 'All' : `${value[0].toUpperCase()}${value.slice(1)}s`}
              </button>
            ))}
          </div>
          <select value={state.plan} onChange={(event) => setState({ plan: event.target.value, page: '1' })} aria-label="Plan">
            <option value="all">All plans</option>
            <option value="paying">Paying (Stripe)</option>
            <option value="paid">Marked paid</option>
            <option value="free">Free</option>
          </select>
          <select value={state.site} onChange={(event) => setState({ site: event.target.value, page: '1' })} aria-label="Signed up on">
            <option value="all">Signed up anywhere</option>
            <option value="readers">Signed up on Readers</option>
            <option value="worksheets">Came from worksheets</option>
          </select>
          <select value={state.activity} onChange={(event) => setState({ activity: event.target.value, page: '1' })} aria-label="Reading activity">
            <option value="all">Any activity</option>
            <option value="with">Opened a story</option>
            <option value="without">No stories yet</option>
          </select>
          <DateRange from={state.from} to={state.to} onChange={(range) => setState({ ...range, page: '1' })} />
          {filtersActive && (
            <button className="btn--ghost btn--sm" onClick={clearFilters}>
              <FiX /> Clear
            </button>
          )}
        </div>
        <div className={`progress-bar${list.loading && list.data ? ' is-active' : ''}`} />
        <DataTable
          columns={columns}
          rows={list.data ? list.data.users : []}
          rowKey={(user) => user._id}
          loading={list.loading}
          initialLoading={list.loading && !list.data}
          sort={state.sort}
          dir={state.dir as 'asc' | 'desc'}
          onSort={onSort}
          onRowClick={(user) => setState({ user: user._id })}
          selectedKey={state.user || null}
          skeletonRows={Math.min(query.limit, 12)}
          emptyTitle={filtersActive ? 'No reading users match these filters' : 'No reading users'}
        />
        <Pagination
          page={query.page}
          totalPages={list.data ? list.data.totalPages : 1}
          total={list.data ? list.data.total : 0}
          limit={query.limit}
          loading={list.loading}
          onPage={(page) => setState({ page: String(page) })}
          onLimit={(limit) => setState({ limit: String(limit), page: '1' })}
        />
      </div>
      {state.user && (
        <ReaderDrawer
          id={state.user}
          preview={list.data ? list.data.users.find((user) => user._id === state.user) : undefined}
          onClose={() => setState({ user: '' })}
        />
      )}
    </>
  );
};

const ReaderDrawer: React.FC<{ id: string; preview?: ReaderRow; onClose: () => void }> = ({ id, preview, onClose }) => {
  const detail = useCachedQuery(`rd:user:${id}`, (signal) => fetchReader(id, signal), 60000);
  const user = detail.data || preview;
  return (
    <Drawer
      title={user ? user.name || user.email || 'Reading user' : <Skeleton width="20rem" height="2rem" />}
      subtitle={user && user.name ? user.email : undefined}
      avatar={<span className="avatar">{user ? initialsOf(user.name, user.email) : ''}</span>}
      onClose={onClose}
    >
      {user && user.email && (
        <div style={{ display: 'flex', gap: '0.8rem', marginBottom: '2.4rem' }}>
          <a className="btn btn--secondary btn--sm" href={`mailto:${user.email}`}>
            <FiMail /> Email
          </a>
        </div>
      )}
      <div className="drawer__section">
        <h3>Account</h3>
        {user ? (
          <dl className="kv">
            <dt>Type</dt>
            <dd style={{ textTransform: 'capitalize' }}>{user.accountType}</dd>
            <dt>Plan</dt>
            <dd>
              <PlanBadge plan={user.plan} />
              {user.nextBillingDate && <span className="muted"> renews {formatDate(user.nextBillingDate)}</span>}
            </dd>
            <dt>Payment</dt>
            <dd>{user.plan === 'free' ? (user.openedPayment ? 'Opened the payment modal' : 'Did not open the payment modal') : 'Subscribed'}</dd>
            <dt>Signed up</dt>
            <dd>{formatDateTime(user.createdAt)}</dd>
            <dt>Last active</dt>
            <dd>{user.lastActive ? withAgo(user.lastActive) : '–'}</dd>
            <dt>Sign in with</dt>
            <dd>{user.authProviders && user.authProviders.length ? user.authProviders.join(', ') : 'Password'}</dd>
            {user.accountType !== 'student' && (
              <>
                <dt>Signed up on</dt>
                <dd><SignupSiteBadge site={user.signupSite} /></dd>
              </>
            )}
            <dt>Device</dt>
            <dd>{user.device || '–'}</dd>
          </dl>
        ) : (
          <Skeleton width="100%" height="12rem" block />
        )}
      </div>
      <div className="drawer__section">
        <h3>Children</h3>
        {!detail.data && !detail.error && <Skeleton width="60%" />}
        {detail.error && <Notice tone="error">Could not load details: {detail.error.message}</Notice>}
        {detail.data && detail.data.children.length === 0 && <p className="muted">No child profiles.</p>}
        {detail.data && detail.data.children.length > 0 && (
          <ul className="timeline">
            {detail.data.children.map((child) => (
              <li key={child._id}>
                <span className="timeline__dot" />
                <div>
                  <div className="timeline__title">{child.name || 'Unnamed child'}</div>
                  <div className="timeline__meta">
                    {gradeLabel(child.grade)}
                    {child.username ? ` · ${child.username}` : ''}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <ReaderActivitySection id={id} />
      {detail.data && <StoriesOpenedSection stories={detail.data.stories} />}
      {detail.data && <WordsTappedSection words={detail.data.trackedWords} />}
    </Drawer>
  );
};

const CouponModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [code, setCode] = useState('');
  const [duration, setDuration] = useState('one month');
  const [status, setStatus] = useState<{ tone: 'error' | 'success'; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim()) {
      setStatus({ tone: 'error', message: 'Enter a code.' });
      return;
    }
    setBusy(true);
    try {
      const response = await createCouponCode(code.trim(), duration);
      setStatus({ tone: 'success', message: response.message || 'Coupon code created.' });
    } catch (err) {
      setStatus({ tone: 'error', message: (err as Error).message });
    }
    setBusy(false);
  };

  return (
    <Modal title="Create a Readers coupon code" onClose={onClose}>
      <p>Parents redeem the code in No Fuss Readers for free paid access.</p>
      <form onSubmit={submit} style={{ marginTop: '1.6rem', display: 'grid', gap: '1.4rem' }}>
        <label className="field">
          <span>Code</span>
          <input type="text" value={code} onChange={(event) => setCode(event.target.value)} placeholder="SPRING2027" autoFocus />
        </label>
        <label className="field">
          <span>Duration</span>
          <select value={duration} onChange={(event) => setDuration(event.target.value)}>
            <option value="one month">One month</option>
            <option value="three months">Three months</option>
          </select>
        </label>
        {status && <Notice tone={status.tone}>{status.message}</Notice>}
        <div className="ui-modal__actions" style={{ marginTop: 0 }}>
          <button type="button" className="btn--secondary" onClick={onClose}>
            {status && status.tone === 'success' ? 'Done' : 'Cancel'}
          </button>
          <button type="submit" className="btn--coral" disabled={busy}>
            {busy ? 'Creating…' : 'Create code'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ReadingPortalUsers;
